// React and related imports
import React, { useMemo } from 'react';

// UI components and icons
import { Button, Dropdown } from 'flowbite-react';
import Flag from 'react-world-flags';

// Interfaces and utility functions
import { ILanguageOption } from '../../common/interfaces/LanguageOption.interface';
import { switchLanguage } from '../../utils/languageUtils';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';
import { togglePageDirection } from '../../redux/slices/displaySettingsSlice';

// i18n for translations
import { useTranslation } from 'react-i18next';

// Constants
import { languageOptions } from '../../common/constants/languageOptions';

export const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const dispatch = useAppDispatch();

  const currentLanguageCode = i18n.language;

  const currentLanguage = useMemo(
    () =>
      languageOptions.find((lang) => lang.languageCode === currentLanguageCode),
    [currentLanguageCode]
  );

  const handleLanguageChange = (lang: ILanguageOption): void => {
    switchLanguage(lang.languageCode);
    dispatch(togglePageDirection(lang.languageCode));
  };

  const buttonClassName = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  return (
    <Dropdown
      arrowIcon={false}
      inline
      label={
        <Button size="xs" className={buttonClassName} as={'span'}>
          {currentLanguage && (
            <>
              <Flag
                code={currentLanguage.flagCode}
                className="me-2 h-4 w-4 rounded-full"
              />
              <span className="mr-2">{currentLanguage.label}</span>
            </>
          )}
        </Button>
      }
    >
      {languageOptions.map((lang) => (
        <Dropdown.Item
          key={lang.languageCode}
          onClick={() => handleLanguageChange(lang)}
          disabled={lang.languageCode === currentLanguageCode}
          className={`${
            lang.languageCode === currentLanguageCode
              ? 'cursor-not-allowed opacity-50'
              : ''
          }`}
        >
          <Flag
            code={lang.flagCode}
            className="me-2 h-3.5 w-3.5 rounded-full"
          />
          <span className="mr-2">{lang.label}</span>
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
};
